/**
 * /smartlinks/pub-list — SmartLink publisher whitelist / blacklist page.
 *
 * Declares a TanStack Router route and renders the headline +
 * `SmartlinkPubListPage`. Route auto-registers against the scaffolded
 * `rootRoute` via the scaffold's `router.tsx` — the root wire-up cycle
 * picks it up by importing from this module.
 *
 * Mirrors the structure of apps/dash/src/routes/campaigns.os-targeting.tsx:
 *   - validateSearch reserves ?smartlinkId=… for deep-linking.
 *   - Page shell = Breadcrumb + Heading + form card + DocHint.
 *   - No competitor names, no emojis, light theme, brand gradient on H1.
 */

import { createRoute, Link } from "@tanstack/react-router";
import { rootRoute } from "./__root";
import { SmartlinkPubListPage } from "@/features/smartlinks-pub-list/SmartlinkPubListPage";

// ---------------------------------------------------------------------------
// Route — mounts at `/smartlinks/pub-list`.
// `validateSearch` reserves room for a `?smartlinkId=` deep-link so operators
// can open the page from a SmartLink detail view with the id prefilled.
// ---------------------------------------------------------------------------

interface SmartlinkPubListSearch {
	smartlinkId?: string;
}

export const smartlinksPubListRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/smartlinks/pub-list",
	validateSearch: (raw: Record<string, unknown>): SmartlinkPubListSearch => {
		const smartlinkId = raw.smartlinkId;
		if (typeof smartlinkId === "string" && smartlinkId.trim().length > 0) {
			return { smartlinkId: smartlinkId.trim() };
		}
		return {};
	},
	component: SmartlinksPubListPage,
});

export default smartlinksPubListRoute;

// ---------------------------------------------------------------------------
// Page — static shell + feature card. `apiClient` attaches `x-tenant-id`.
// ---------------------------------------------------------------------------

export function SmartlinksPubListPage() {
	const { smartlinkId } = smartlinksPubListRoute.useSearch();

	return (
		<main className="mx-auto flex w-full max-w-4xl flex-col gap-8 px-6 py-10 sm:py-16">
			<Breadcrumb />
			<Heading />
			<SmartlinkPubListPage initialSmartlinkId={smartlinkId} />
			<DocHint />
		</main>
	);
}

// ---------------------------------------------------------------------------
// Header fragments — purely presentational, brand rules enforced.
// ---------------------------------------------------------------------------

function Breadcrumb() {
	return (
		<nav aria-label="Breadcrumb" className="text-sm text-zinc-500">
			<ol className="flex flex-wrap items-center gap-1">
				<li>
					<Link
						to="/"
						className="rounded-[6px] px-1 py-0.5 hover:bg-zinc-100 hover:text-zinc-900"
					>
						Dashboard
					</Link>
				</li>
				<li aria-hidden="true" className="text-zinc-300">
					/
				</li>
				<li>
					<span className="rounded-[6px] px-1 py-0.5">SmartLinks</span>
				</li>
				<li aria-hidden="true" className="text-zinc-300">
					/
				</li>
				<li className="text-zinc-900">Publisher lists</li>
			</ol>
		</nav>
	);
}

function Heading() {
	return (
		<header className="flex flex-col gap-2">
			<p className="text-xs font-semibold uppercase tracking-[0.12em] text-zinc-500">
				SmartLinks
			</p>
			<h1 className="text-brand-gradient text-3xl font-semibold tracking-tight sm:text-4xl">
				Publisher allow / deny lists
			</h1>
			<p className="max-w-prose text-sm text-zinc-600">
				Restrict which publishers may drive traffic through a SmartLink. Allow
				list mode accepts only the listed publishers; deny list mode blocks
				the listed ones. Rules apply to the next click — no deploy step.
			</p>
		</header>
	);
}

function DocHint() {
	return (
		<aside className="rounded-[8px] border border-zinc-200 bg-zinc-50 p-4 text-sm text-zinc-600">
			<p className="font-medium text-zinc-800">How the modes interact</p>
			<ul className="mt-2 flex list-inside list-disc flex-col gap-1">
				<li>
					When any allow entries exist, the SmartLink is in allow mode and
					only those publishers pass.
				</li>
				<li>
					When there are no allow entries, deny entries block the listed
					publishers and everyone else is allowed.
				</li>
				<li>
					To change a publisher's list kind, delete the existing row and
					re-add it under the new mode.
				</li>
			</ul>
		</aside>
	);
}
